import React, { useState, useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Banner } from 'components/Atoms/Banner/Banner';
import { Flex } from 'components/Atoms/Flex/Flex';
import { Actions, PageContext } from '@/context/PageContext';
import { Icon } from 'components/Atoms/Icon/Icon';
import { LogWarning } from 'utils/loggingHelpers';
import { buildExternalPreviewShareUrl } from 'utils/externalPreviewShareUrl';
import { resolvePreviewEntity } from '@/constants/previewEntities';
import { PREVIEW_BANNER_KEYS, resolvePreviewBannerCopy, resolvePreviewView } from './previewBannerCopy';
import { PreviewShareControl } from './PreviewShareControl';
import { useFormattedPreviewDate } from './useFormattedPreviewDate';
import styles from './SurveyPreviewBanner.module.scss';

export interface Props {
  noContainer?: boolean;
}

const BOLD = { strong: <strong /> };

const buildShareUrl = (
  slug: string[] | undefined,
  code: string | undefined,
  canonicalHostname: string | null | undefined,
  published: boolean,
) => {
  if (!slug || !code) {
    return undefined;
  }
  try {
    return buildExternalPreviewShareUrl(slug, code, canonicalHostname, { published });
  } catch (error) {
    LogWarning('Failed to build external preview URL', {
      slug: slug.join('/'),
      error: error instanceof Error ? error.message : String(error),
    });
    return undefined;
  }
};

const focusMainContent = () => {
  const main = document.querySelector('main');
  if (main) {
    main.setAttribute('tabindex', '-1');
    main.focus();
  }
};

/**
 * Tells the viewer which version of the entity they are looking at, whether it collects anything,
 * and, on the admin's own preview, offers the link to share it outside the organization.
 */
export function SurveyPreviewBanner({ noContainer }: Props) {
  const [hide, setHide] = useState(false);
  const { urlParams, survey, questionnaire, page, dispatch, auth0Token, canonicalHostname, slug } =
    useContext(PageContext);
  const { t } = useTranslation();
  const shareSource = survey ?? questionnaire ?? page;
  const view = resolvePreviewView(slug, urlParams);
  const expiry = useFormattedPreviewDate(shareSource?.external_preview_expires_at, urlParams?.locale);
  const releaseDate = useFormattedPreviewDate(page?.released_at, urlParams?.locale, { upcomingOnly: true });

  if (hide || !urlParams?.preview) {
    return null;
  }

  // Shared from a published view, the link opens the same published version.
  const shareUrl = buildShareUrl(slug, shareSource?.external_preview_code, canonicalHostname, view === 'publishedLink');
  const copy = resolvePreviewBannerCopy({
    view,
    entity: resolvePreviewEntity(slug, urlParams),
    slug,
    hasAuth0Token: Boolean(auth0Token),
    hasShareCode: Boolean(shareUrl),
    hasReleaseDate: Boolean(releaseDate),
  });

  const handleOnHide = () => {
    setHide(true);
    focusMainContent();
    if (dispatch) {
      dispatch({ type: Actions.USER_INTERACTION });
    }
  };

  return (
    <Banner id="SurveyPreview" noContainer={noContainer} role="region" ariaLabel={t(PREVIEW_BANNER_KEYS.noticeLabel)}>
      <Flex justifyContent="space-between" alignItems="center" flexWrap="wrap" className={styles.container}>
        <div>
          <p className={styles.message}>
            <Trans
              i18nKey={copy.messageKey}
              components={BOLD}
              values={copy.namesReleaseDate ? { date: releaseDate } : undefined}
            />
            {copy.collectionNoticeKey && <> {t(copy.collectionNoticeKey)}</>}
          </p>
          {copy.showsLinkExpiry && (
            <p className={styles.message}>
              {expiry
                ? t(PREVIEW_BANNER_KEYS.sharedExpiry, { date: expiry })
                : t(PREVIEW_BANNER_KEYS.sharedExpiryUnknown)}
            </p>
          )}
        </div>
        <Flex gap={20} alignItems="center" flexShrink={0}>
          {copy.canShare && shareUrl && <PreviewShareControl shareUrl={shareUrl} expiry={expiry} />}
          {copy.canDismiss && (
            <button
              type="button"
              onClick={handleOnHide}
              data-testid="close-preview-button"
              aria-label={t(PREVIEW_BANNER_KEYS.dismiss)}
            >
              <Icon name="close" ariaHidden />
            </button>
          )}
        </Flex>
      </Flex>
    </Banner>
  );
}
